import React from 'react';

import {FloatingTree, useFloatingParentNodeId} from '@floating-ui/react';

import {FlatButton} from '@pb/kit-component-button';
import {Icon, Icons} from '@pb/kit-component-icon';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {DropdownMenuBase} from './dropdown-menu-base';
import styles from './styles.module.styl';
import {useDropdownMenu} from './use-dropdown-menu';

import type {DropdownMenuBaseProps} from './dropdown-menu-base';
import type {UseDropdownMenuReturn} from './use-dropdown-menu';
import type {Placement, UseFloatingOptions} from '@floating-ui/react';
import type {FlatButtonProps} from '@pb/kit-component-button';
import type {UseDropdownProps} from '@pb/kit-component-dropdown-v2';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';


const classNames = withCssModule(styles);

// TODO: It needs to be updated to provide a default onToggle handler from the div element.
type NativeProps = Omit<ComponentPropsWithoutRef<'div'>, 'role' | 'onToggle'>;

export interface DropdownMenuProps extends NativeProps {

    /**
     * Render prop for the trigger element. Receives `anchorRef`, `getAnchorProps`,
     * `positionRef`, and `defaultAnchorProps` — spread them onto your trigger node.
     * When omitted, a default `<FlatButton>` with a chevron-down icon is used.
     */
    anchor: (props: {
        anchorRef: UseDropdownMenuReturn['anchorRef'],
        /** Merges Floating UI interaction props (click, hover, focus) onto an
         *  arbitrary set of element props without overwriting caller-supplied handlers. */
        getAnchorProps: UseDropdownMenuReturn['getAnchorProps'],
        positionRef: UseDropdownMenuReturn['positionRef'],
        defaultAnchorProps?: FlatButtonProps,
    }) => React.JSX.Element,

    /**
     * Non-interactive heading displayed at the top of the floating panel.
     * Use to label the purpose of the menu (e.g. `"Actions"`, `"Sort by"`).
     */
    title?: string,

    /**
     * `<DropdownMenuItem>` elements that populate the menu. Nested sub-menus are
     * created by passing `<DropdownMenuItem>` children inside another
     * `<DropdownMenuItem>` — the component promotes those to a `<NestedMenu>`.
     */
    children: ReactNode,

    /**
     * Component used to wrap `children` inside the floating panel. Defaults to
     * the built-in menu body `<div>`. Override to add scrolling or custom padding.
     */
    bodyComponent?: DropdownMenuBaseProps['bodyComponent'],

    /**
     * Props forwarded to `FloatingPortal`. Use to control the portal mount target
     * or add a class to the portal wrapper `<div>`.
     */
    portalProps?: DropdownMenuBaseProps['portalProps'],

    /**
     * Props forwarded to Floating UI's `FloatingFocusManager` (e.g. `initialFocus`,
     * `returnFocus`, `modal`). Controls focus trapping behavior inside the panel.
     */
    floatingFocusManagerProps?: DropdownMenuBaseProps['floatingFocusManagerProps'],

    /**
     * When `false`, keeps the menu content mounted in the DOM while hidden.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. render your modals outside of the dropdown, see example in the Storybook
     */
    shouldUnmount?: boolean,

    /**
     * Floating UI positioning strategy. Use `"FIXED"` inside scrollable containers
     * or modals to avoid clipping; `"ABSOLUTE"` (default) is more performant for
     * standard page layouts.
     */
    type?: 'ABSOLUTE' | 'FIXED',

    /**
     * Controlled open state. When provided, the component does not manage its
     * own visibility — pair with `setShown` to form a controlled pattern.
     */
    shown?: boolean,

    /**
     * Setter for the controlled `shown` state. Required when `shown` is provided.
     */
    setShown?: (shown: boolean) => unknown,

    /**
     * When `true`, the menu opens on pointer enter instead of click.
     * Also configures `FloatingFocusManager` to not return focus on close,
     * preventing the browser from scrolling back to the last-clicked element.
     *
     * @deprecated It will be removed in 6 months 03-15-2026. Use `hoverEnabled` or `clickEnabled` instead.
     */
    hoverable?: boolean,

    /**
     * Maximum height of the floating panel in pixels. When the menu content
     * exceeds this value, the body becomes scrollable.
     */
    maxHeight?: number,

    /**
     * Maximum width of the floating panel in pixels.
     */
    maxWidth?: number,

    /**
     * Vertical offset (in px) applied to the floating element's position relative
     * to the anchor. Positive values move the panel down.
     */
    verticalOffset?: number,

    /**
     * Horizontal offset (in px) applied to the floating element's position relative
     * to the anchor. Positive values move the panel to the right.
     */
    horizontalOffset?: number,

    /**
     * Called whenever the menu opens or closes. Receives `true` when opening,
     * `false` when closing. Use as a lightweight alternative to `shown`+`setShown`
     * when you only need to react to open/close events without controlling state.
     */
    onToggle?: (shown: boolean) => unknown,

    /**
     * When `true` (default for hoverable menus), closes the panel when the
     * pointer leaves the floating element.
     */
    hideOnBodyLeave?: boolean,

    /**
     * WAI-ARIA role for the floating panel. Set to `"menu"` for keyboard-navigable
     * item lists, `"dialog"` for modal-like overlays, or `"listbox"` for selection
     * widgets. Defaults to `"menu"` in `useDropdownMenu`.
     */
    role?: 'tooltip' | 'dialog' | 'alertdialog' | 'menu' | 'listbox' | 'grid' | 'tree' | 'select' | 'label' | 'combobox',

    /**
     * When `true`, enables Floating UI's `useListNavigation` so arrow keys move
     * focus among menu items. Required for keyboard-navigable item lists.
     */
    listNavigationEnabled?: boolean,

    /**
     * Ordered list of Floating UI placements the panel may occupy. The first
     * placement that fits the viewport is selected automatically.
     */
    allowedPlacements?: Placement[],

    /**
     * Additional Floating UI middleware (e.g. `offset`, `shift`, `hide`) appended
     * to the default middleware stack.
     */
    middlewares?: UseFloatingOptions['middleware'],

    /**
     * When `true`, clicking any `<DropdownMenuItem>` closes all open menu levels
     * (root + any nested sub-menus). Useful for navigation menus where the whole
     * tree should dismiss after a selection.
     */
    hideOnItemClick?: boolean,

    /**
     * Props merged onto the default `<FlatButton>` anchor when no `anchor` render
     * prop is provided. Ignored when a custom `anchor` is supplied.
     */
    defaultAnchorProps?: FlatButtonProps,

    /**
     * When `true`, the component is treated as a nested sub-menu: it opens on
     * hover (via `useHover`) and positions itself to the side of its parent item
     * rather than below the anchor. Set automatically by `<NestedMenu>`.
     */
    nested?: boolean,

    /**
     * Props forwarded to Floating UI's `useHover` hook. Use to tune delay,
     * handleClose strategy, or whether moving the pointer to the floating panel
     * keeps it open. See {@link https://floating-ui.com/docs/useHover}.
     */
    hoverProps?: UseDropdownProps['hoverProps'],
}

const getDefaultAnchor: DropdownMenuProps['anchor'] = ({anchorRef, getAnchorProps, positionRef, defaultAnchorProps}) => (
    <FlatButton
        className={classNames('default-anchor')}
        variant="secondary"
        endIcon={<Icon size="Size_14" type={Icons.ANGLE_DOWN} ref={positionRef}/>}
        {...getAnchorProps(defaultAnchorProps)}
        ref={anchorRef}
    />
);

const DropdownMenuInner = ({
    defaultAnchorProps,
    anchor = getDefaultAnchor,
    children,
    floatingFocusManagerProps,
    maxHeight,
    maxWidth,
    verticalOffset,
    horizontalOffset,
    role,
    allowedPlacements,
    type,
    shown,
    setShown,
    onToggle,
    hideOnBodyLeave,
    hoverable,
    middlewares,
    listNavigationEnabled,
    hideOnItemClick,
    nested,
    hoverProps,
    ...nativeProps
}: DropdownMenuProps) => {
    const {
        getAnchorProps,
        getDropdownMenuProps,
        dropdownRef,
        anchorRef,
        positionRef,
    } = useDropdownMenu({
        hideOnItemClick,
        maxHeight,
        maxWidth,
        verticalOffset,
        horizontalOffset,
        nested,
        role,
        allowedPlacements,
        type,
        shown,
        setShown,
        onToggle,
        hideOnBodyLeave,
        hoverable,
        middlewares,
        listNavigationEnabled,
        hoverProps,
    });

    return (
        <>
            {anchor({
                anchorRef,
                getAnchorProps,
                positionRef,
                defaultAnchorProps,
            })}
            <DropdownMenuBase
                {...getDropdownMenuProps(nativeProps)}
                floatingFocusManagerProps={{
                    ...(hoverable ? {returnFocus: false} : {}), // without this it returns focus to the last clicked element and browser scrolls to it
                    ...floatingFocusManagerProps,
                }}
                ref={dropdownRef}>
                {children}
            </DropdownMenuBase>
        </>
    );
};

/**
 * Dropdown panel pre-configured for keyboard-navigable `MenuItem` lists, with
 * built-in support for nested sub-menus.
 *
 * Wraps `Dropdown` with `FloatingTree` (auto-applied at the root level) and
 * `useDropdownMenu`, which wires up Floating UI's list-navigation and item-click
 * dismissal. Pass `MenuItem` components as `children`; use the `nested` prop on
 * a child `DropdownMenu` to create a sub-menu that opens on hover.
 *
 * The trigger element is supplied via the required `anchor` render prop, which
 * receives `anchorRef`, `getAnchorProps`, and `positionRef` — spread these onto
 * your trigger. A default `FlatButton` trigger is rendered when `anchor` is omitted.
 *
 * Set `hideOnItemClick` to close all menu levels when any item is selected.
 * Use `hoverable` to open the menu on pointer enter instead of click.
 * Supports controlled open state via `shown` + `setShown`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-dropdown-menu--documentation}
 */
export const DropdownMenu = (props: DropdownMenuProps) => {
    const parentId = useFloatingParentNodeId();

    if (parentId === null) {
        return (
            <FloatingTree>
                <DropdownMenuInner {...props}/>
            </FloatingTree>
        );
    }

    return <DropdownMenuInner {...props}/>;
};

DropdownMenu.displayName = 'DropdownMenu';
